import React, { useState, useEffect } from "react";
import { Link, NavLink, useNavigate, useLocation } from "react-router-dom";
import { ShoppingBag, Menu, X, User } from "lucide-react";
import { useCart } from "../lib/CartContext";
import { useCustomerAuth } from "../lib/CustomerAuthContext";

const PDOB_LOGO = `${process.env.REACT_APP_BACKEND_URL}/api/static/pdob-street-logo.png`;

const NAV_ITEMS = [
  { to: "/", label: "HOME", testid: "nav-home", end: true },
  { to: "/shop", label: "SHOP", testid: "nav-shop" },
  { to: "/coming-soon", label: "COMING SOON", testid: "nav-coming-soon" },
  { to: "/reviews", label: "REVIEWS", testid: "nav-reviews" },
  { to: "/contact", label: "CONTACT", testid: "nav-contact" },
];

const NavBar = () => {
  const { count } = useCart();
  const { customer } = useCustomerAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [open, setOpen] = useState(false);

  // Close the mobile menu whenever the route changes
  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  const linkClass = ({ isActive }) =>
    `relative font-mono-tech text-xs uppercase tracking-widest px-3 py-2 transition-colors after:content-[''] after:absolute after:left-3 after:right-3 after:-bottom-0.5 after:h-[1px] after:transition-transform after:origin-left ${
      isActive
        ? "text-zinc-50 after:bg-zinc-100 after:scale-x-100"
        : "text-zinc-400 hover:text-zinc-100 after:bg-zinc-500 after:scale-x-0 hover:after:scale-x-100"
    }`;

  const mobileLinkClass = ({ isActive }) =>
    `block font-mono-tech text-sm uppercase tracking-widest px-4 py-3 border-l-4 ${
      isActive
        ? "border-zinc-200 text-zinc-200 bg-zinc-900"
        : "border-transparent text-zinc-200 hover:border-zinc-300 hover:bg-zinc-900"
    } transition-colors`;

  return (
    <nav className="sticky top-0 z-50 bg-zinc-950 border-b-2 border-zinc-800" data-testid="main-nav">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between h-20">
        <Link to="/" className="flex items-center gap-3 sm:gap-4 group shrink-0" data-testid="nav-home-link">
          <img
            src={PDOB_LOGO}
            alt="PDOB Street Wear"
            className="h-11 sm:h-14 w-auto object-contain drop-shadow-[0_2px_10px_rgba(0,0,0,0.6)] transition-transform duration-300 ease-out group-hover:scale-[1.04]"
            data-testid="nav-brand-logo"
          />
          <div className="hidden sm:flex flex-col leading-none border-l border-zinc-700/70 pl-3">
            <span className="font-syne font-bold text-[15px] text-zinc-50 tracking-[0.28em]">PANDEMIC</span>
            <span className="font-mono-tech text-[9px] text-zinc-500 tracking-[0.5em] mt-1.5 uppercase">Street Wear</span>
          </div>
        </Link>

        {/* Desktop nav */}
        <div className="hidden md:flex items-center gap-2" data-testid="desktop-nav-links">
          {NAV_ITEMS.map((item) => (
            <NavLink key={item.to} to={item.to} end={item.end} className={linkClass} data-testid={item.testid}>
              {item.label}
            </NavLink>
          ))}
        </div>

        <div className="flex items-center gap-2">
          {customer ? (
            <button
              type="button"
              onClick={() => navigate("/account")}
              className="flex items-center gap-2 px-3 py-2 border-2 border-zinc-800 hover:border-zinc-300 transition-colors"
              data-testid="nav-account-button"
              title={customer.name}
            >
              {customer.picture ? (
                <img src={customer.picture} alt="" className="w-5 h-5 rounded-full" />
              ) : (
                <User className="w-4 h-4 text-zinc-200" />
              )}
              <span className="hidden sm:inline font-mono-tech text-xs uppercase text-zinc-200 truncate max-w-[6rem]">{customer.name.split(" ")[0]}</span>
            </button>
          ) : (
            <button
              type="button"
              onClick={() => navigate("/account")}
              className="hidden sm:flex items-center gap-2 px-3 py-2 border-2 border-zinc-800 hover:border-zinc-300 transition-colors"
              data-testid="nav-signin-button"
            >
              <User className="w-4 h-4 text-zinc-200" />
              <span className="font-mono-tech text-xs uppercase text-zinc-200">Sign in</span>
            </button>
          )}

          <button
            type="button"
            onClick={() => navigate("/cart")}
            className="relative flex items-center gap-2 px-3 py-2 border-2 border-zinc-800 hover:border-zinc-300 transition-colors"
            data-testid="nav-cart-button"
          >
            <ShoppingBag className="w-4 h-4 text-zinc-200" />
            <span className="hidden sm:inline font-mono-tech text-xs uppercase text-zinc-200">Cart</span>
            {count > 0 && (
              <span
                className="absolute -top-2 -right-2 bg-zinc-300 text-zinc-950 font-mono-tech text-xs px-1.5 py-0.5 border-2 border-zinc-950"
                data-testid="cart-count-badge"
              >
                {count}
              </span>
            )}
          </button>

          {/* Mobile menu toggle */}
          <button
            type="button"
            onClick={() => setOpen((o) => !o)}
            className="md:hidden flex items-center justify-center w-10 h-10 border-2 border-zinc-800 hover:border-zinc-300 transition-colors"
            data-testid="nav-mobile-toggle"
            aria-expanded={open}
            aria-label="Toggle menu"
          >
            {open ? <X className="w-5 h-5 text-zinc-200" /> : <Menu className="w-5 h-5 text-zinc-100" />}
          </button>
        </div>
      </div>

      {/* Mobile menu drawer */}
      {open && (
        <div className="md:hidden border-t-2 border-zinc-800 bg-zinc-950" data-testid="mobile-nav-drawer">
          <div className="py-2">
            {NAV_ITEMS.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.end}
                className={mobileLinkClass}
                data-testid={`mobile-${item.testid}`}
              >
                {item.label}
              </NavLink>
            ))}
          </div>
        </div>
      )}
    </nav>
  );
};

export default NavBar;
